'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale } from 'next-intl';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { confirmRegistration, resendCode } from '../lib';

import { pushSimpleGAEvent } from '@components/Analytics/Google/GAScript';
import { Button, Input } from '@components/Form';
import { logger } from '@logger';

const CODE_RESEND_COOLDOWN = 45;

const schema = z.object({
  code: z.string().length(6),
});

export interface ActivateAccountFormProps {
  email: string;
  className?: string;
  onSuccess?: () => void;
}

export default function ActivateAccountForm({
  email,
  onSuccess,
  className,
}: ActivateAccountFormProps) {
  const locale = useLocale();
  const [resendCodeCooldown, setResendCodeCooldown] =
    useState(CODE_RESEND_COOLDOWN);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const t = useTranslations('auth');

  const onSubmit = handleSubmit(async ({ code }) => {
    const { success, error } = await confirmRegistration(email, code, locale);

    if (success) {

      pushSimpleGAEvent('transinfo_old_register_step_2', {
        transinfo_is_logged_in: 'false',
        transinfo_is_newsletter_user: 'false',
        transinfo_user_email: email,
        transinfo_user_provider: 'wordpress',
        locale: locale
      });

      if (onSuccess) {
        onSuccess();
      }
      return;
    }

    if (error === 'invalidCode') {
      setError('code', {
        message: t('activate.errors.invalidCode'),
      });
    } else {
      setError('code', {
        message: t('activate.errors.error'),
      });
    }
  });

  const handleResendCode = async () => {
    if (resendCodeCooldown > 0) {
      return;
    }

    setResendCodeCooldown(CODE_RESEND_COOLDOWN);

    const success = await resendCode(email, locale);

    if (!success) {
      logger.error('Failed to resend code.');
      // TODO: notify user
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (resendCodeCooldown <= 0) {
        return;
      }

      setResendCodeCooldown((value) => Math.max(0, value - 1));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  });

  return (
    <form onSubmit={onSubmit} className={className}>
      <h2 className="text-md mb-3 font-bold text-premium-900 lg:mb-4 lg:text-xl dark:text-white">
        {t('activate.title')}
      </h2>

      <div className="mb-2 lg:mb-3">
        <Input
          className="mb-1"
          type="text"
          placeholder={t('activate.code')}
          error={!!errors.code}
          {...register('code')}
        />
        {errors.code && (
          <p className="text-xs text-red-500">{errors.code.message}</p>
        )}
        <button
          type="button"
          onClick={handleResendCode}
          className="mb-2 text-sm font-bold text-premium-900 underline disabled:cursor-not-allowed disabled:opacity-70 dark:text-white"
          disabled={resendCodeCooldown > 0}
        >
          {t('activate.resendCode')}
          {resendCodeCooldown > 0 && ` (${resendCodeCooldown})`}
        </button>
      </div>

      <div className="flex flex-col justify-center items-center">
        <Button
          className="mb-1 lg:mb-2"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {t('label.next')}
        </Button>
      </div>

      {locale === 'pl' && (
        <p className="text-xs text-premium-900 dark:text-gray-200">
          {t('activate.didntReceiveTheCode')}
        </p>
      )}
    </form>
  );
}
